import { useState, useCallback, useEffect } from 'react';
import { FREQUENCY_COLORS, FREQUENCY_NAMES, FREQUENCY_GLOW_COLORS } from '@/types';

// ── Helpers ──

function getColor(freq: number): string {
  return FREQUENCY_COLORS[freq] ?? '#888';
}

function getGlow(freq: number): string {
  return FREQUENCY_GLOW_COLORS[freq] ?? '#aaa';
}

function getName(freq: number): string {
  return FREQUENCY_NAMES[freq] ?? '?';
}

// ── Types ──

interface TutorialSlide {
  title: string;
  subtitle: string;
  visual: React.ReactNode;
}

interface TutorialProps {
  onComplete: () => void;
}

// ── Slide Visuals ──

function NetworkVisual() {
  // 4 glowing orbs in a diamond pattern with connecting lines
  const nodes = [
    { x: 50, y: 20, freq: 1 },  // C - top
    { x: 20, y: 50, freq: 5 },  // E - left
    { x: 80, y: 50, freq: 7 },  // F# - right
    { x: 50, y: 80, freq: 3 },  // D - bottom
  ];

  const lines: [number, number][] = [
    [0, 1], [0, 2], [1, 3], [2, 3], [0, 3],
  ];

  return (
    <div style={visualContainerStyle}>
      <svg
        viewBox="0 0 100 100"
        style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}
      >
        {lines.map(([a, b], i) => (
          <line
            key={i}
            x1={nodes[a]?.x}
            y1={nodes[a]?.y}
            x2={nodes[b]?.x}
            y2={nodes[b]?.y}
            stroke="rgba(199, 125, 255, 0.25)"
            strokeWidth="0.5"
          />
        ))}
      </svg>
      {nodes.map((node, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: `${node.x}%`,
            top: `${node.y}%`,
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <div
            style={{
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              background: `radial-gradient(circle at 40% 35%, ${getGlow(node.freq)}, ${getColor(node.freq)})`,
              boxShadow: `0 0 12px ${getColor(node.freq)}80, 0 0 24px ${getColor(node.freq)}40`,
              animation: 'tutorialPulse 3s ease-in-out infinite',
              animationDelay: `${i * 0.4}s`,
            }}
          />
          <span style={{
            fontSize: '10px',
            color: 'rgba(232,232,228,0.6)',
            fontFamily: "system-ui, -apple-system, sans-serif",
            letterSpacing: '0.05em',
          }}>
            {getName(node.freq)}
          </span>
        </div>
      ))}
    </div>
  );
}

function HarmonyVisual() {
  // Two pathway examples: one satisfied, one unsatisfied
  return (
    <div style={visualContainerStyle}>
      {/* Satisfied pathway */}
      <div style={{
        position: 'absolute',
        top: '25%',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: '0',
        width: '70%',
      }}>
        <div style={{
          ...tutorialNodeStyle,
          background: `radial-gradient(circle at 40% 35%, ${getGlow(1)}, ${getColor(1)})`,
          boxShadow: `0 0 10px ${getColor(1)}80`,
        }} />
        <div style={{
          flex: 1,
          height: '2px',
          background: 'linear-gradient(90deg, rgba(81, 207, 102, 0.8), rgba(81, 207, 102, 0.8))',
          boxShadow: '0 0 6px rgba(81, 207, 102, 0.5)',
          position: 'relative',
        }}>
          <span style={{
            position: 'absolute',
            top: '-18px',
            left: '50%',
            transform: 'translateX(-50%)',
            fontSize: '11px',
            color: 'rgba(81, 207, 102, 0.9)',
            fontFamily: "system-ui, -apple-system, sans-serif",
            fontWeight: 500,
          }}>P5</span>
        </div>
        <div style={{
          ...tutorialNodeStyle,
          background: `radial-gradient(circle at 40% 35%, ${getGlow(8)}, ${getColor(8)})`,
          boxShadow: `0 0 10px ${getColor(8)}80`,
        }} />
      </div>
      <div style={{
        position: 'absolute',
        top: '42%',
        left: '50%',
        transform: 'translateX(-50%)',
        fontSize: '10px',
        color: 'rgba(81, 207, 102, 0.7)',
        fontStyle: 'italic',
        fontFamily: "system-ui, -apple-system, sans-serif",
      }}>satisfied</div>

      {/* Unsatisfied pathway */}
      <div style={{
        position: 'absolute',
        top: '60%',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: '0',
        width: '70%',
      }}>
        <div style={{
          ...tutorialNodeStyle,
          background: `radial-gradient(circle at 40% 35%, ${getGlow(1)}, ${getColor(1)})`,
          boxShadow: `0 0 10px ${getColor(1)}40`,
          opacity: 0.6,
        }} />
        <div style={{
          flex: 1,
          height: '2px',
          background: 'repeating-linear-gradient(90deg, rgba(232,232,228,0.2) 0px, rgba(232,232,228,0.2) 6px, transparent 6px, transparent 12px)',
          position: 'relative',
        }}>
          <span style={{
            position: 'absolute',
            top: '-18px',
            left: '50%',
            transform: 'translateX(-50%)',
            fontSize: '11px',
            color: 'rgba(232,232,228,0.35)',
            fontFamily: "system-ui, -apple-system, sans-serif",
            fontWeight: 500,
          }}>P5</span>
        </div>
        <div style={{
          ...tutorialNodeStyle,
          background: `radial-gradient(circle at 40% 35%, ${getGlow(3)}, ${getColor(3)})`,
          boxShadow: `0 0 10px ${getColor(3)}40`,
          opacity: 0.6,
        }} />
      </div>
      <div style={{
        position: 'absolute',
        top: '77%',
        left: '50%',
        transform: 'translateX(-50%)',
        fontSize: '10px',
        color: 'rgba(232,232,228,0.35)',
        fontStyle: 'italic',
        fontFamily: "system-ui, -apple-system, sans-serif",
      }}>unsatisfied</div>
    </div>
  );
}

function StrikeVisual() {
  return (
    <div style={visualContainerStyle}>
      <div style={{
        position: 'absolute',
        top: '45%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}>
        {/* Expanding rings */}
        {[0, 1, 2].map((ring) => (
          <div
            key={ring}
            style={{
              position: 'absolute',
              width: `${40 + ring * 30}px`,
              height: `${40 + ring * 30}px`,
              borderRadius: '50%',
              border: `1px solid rgba(199, 125, 255, ${0.4 - ring * 0.12})`,
              animation: 'tutorialRing 2.5s ease-out infinite',
              animationDelay: `${ring * 0.4}s`,
            }}
          />
        ))}
        {/* Center node */}
        <div style={{
          width: '36px',
          height: '36px',
          borderRadius: '50%',
          background: `radial-gradient(circle at 40% 35%, ${getGlow(9)}, ${getColor(9)})`,
          boxShadow: `0 0 16px ${getColor(9)}80, 0 0 32px ${getColor(9)}40`,
          zIndex: 2,
          position: 'relative',
        }} />
        {/* Tap indicator */}
        <div style={{
          position: 'absolute',
          top: '-40px',
          fontSize: '20px',
          color: 'rgba(232,232,228,0.5)',
          animation: 'tutorialPulse 2s ease-in-out infinite',
        }}>
          {'\u261F'}
        </div>
      </div>
    </div>
  );
}

function PropagationVisual() {
  return (
    <div style={visualContainerStyle}>
      <div style={{
        position: 'absolute',
        top: '45%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}>
        {/* Rings with labels */}
        {[
          { radius: 38, label: '+1', ring: 1 },
          { radius: 64, label: '+2', ring: 2 },
          { radius: 90, label: '+3', ring: 3 },
        ].map(({ radius, label, ring }) => (
          <div key={ring} style={{ position: 'absolute' }}>
            <div style={{
              width: `${radius * 2}px`,
              height: `${radius * 2}px`,
              borderRadius: '50%',
              border: `1px solid rgba(199, 125, 255, ${0.5 - ring * 0.12})`,
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }} />
            <span style={{
              position: 'absolute',
              top: '50%',
              left: `${radius + 4}px`,
              transform: 'translateY(-50%)',
              fontSize: '11px',
              color: 'rgba(199, 125, 255, 0.7)',
              fontFamily: "system-ui, -apple-system, sans-serif",
              fontWeight: 500,
              whiteSpace: 'nowrap',
            }}>
              Ring {ring}: {label}
            </span>
          </div>
        ))}

        {/* Center struck node */}
        <div style={{
          width: '28px',
          height: '28px',
          borderRadius: '50%',
          background: `radial-gradient(circle at 40% 35%, ${getGlow(9)}, ${getColor(9)})`,
          boxShadow: `0 0 16px ${getColor(9)}80, 0 0 28px ${getColor(9)}40`,
          zIndex: 2,
          position: 'relative',
        }} />

        {/* Small nodes on rings */}
        {[
          { angle: -60, ring: 1, freq: 5 },
          { angle: 30, ring: 1, freq: 1 },
          { angle: 150, ring: 2, freq: 6 },
          { angle: -120, ring: 2, freq: 3 },
          { angle: 80, ring: 3, freq: 10 },
        ].map((n, i) => {
          const ringRadii = [0, 38, 64, 90] as const;
          const r = ringRadii[n.ring] ?? 0;
          const rad = (n.angle * Math.PI) / 180;
          const x = Math.cos(rad) * r;
          const y = Math.sin(rad) * r;
          return (
            <div
              key={i}
              style={{
                position: 'absolute',
                width: '16px',
                height: '16px',
                borderRadius: '50%',
                background: `radial-gradient(circle at 40% 35%, ${getGlow(n.freq)}, ${getColor(n.freq)})`,
                boxShadow: `0 0 8px ${getColor(n.freq)}60`,
                left: `${x - 8}px`,
                top: `${y - 8}px`,
                zIndex: 1,
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

function ResonanceVisual() {
  // A small board where all pathways glow golden
  const nodes = [
    { x: 50, y: 15, freq: 1 },
    { x: 20, y: 45, freq: 8 },
    { x: 80, y: 45, freq: 8 },
    { x: 35, y: 80, freq: 5 },
    { x: 65, y: 80, freq: 5 },
  ];

  const lines: [number, number][] = [
    [0, 1], [0, 2], [1, 3], [2, 4], [3, 4], [1, 2],
  ];

  return (
    <div style={visualContainerStyle}>
      <svg
        viewBox="0 0 100 100"
        style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}
      >
        <defs>
          <filter id="goldenGlow">
            <feGaussianBlur stdDeviation="1.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        {lines.map(([a, b], i) => (
          <line
            key={i}
            x1={nodes[a]?.x}
            y1={nodes[a]?.y}
            x2={nodes[b]?.x}
            y2={nodes[b]?.y}
            stroke="rgba(252, 191, 73, 0.7)"
            strokeWidth="1"
            filter="url(#goldenGlow)"
          />
        ))}
      </svg>
      {nodes.map((node, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: `${node.x}%`,
            top: `${node.y}%`,
            transform: 'translate(-50%, -50%)',
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            background: `radial-gradient(circle at 40% 35%, ${getGlow(node.freq)}, ${getColor(node.freq)})`,
            boxShadow: `0 0 10px ${getColor(node.freq)}80, 0 0 20px rgba(252, 191, 73, 0.3)`,
            animation: 'resonanceGlow 2s ease-in-out infinite',
            animationDelay: `${i * 0.3}s`,
          }}
        />
      ))}
      {/* Golden overlay haze */}
      <div style={{
        position: 'absolute',
        inset: 0,
        background: 'radial-gradient(circle at 50% 50%, rgba(252, 191, 73, 0.06) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />
    </div>
  );
}

function BeginVisual() {
  return (
    <div style={{
      ...visualContainerStyle,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <div style={{
        fontSize: '42px',
        fontWeight: 300,
        color: '#e8e8e4',
        letterSpacing: '0.2em',
        textShadow: '0 0 30px rgba(114, 9, 183, 0.6), 0 0 60px rgba(114, 9, 183, 0.3), 0 0 90px rgba(199, 125, 255, 0.15)',
        fontFamily: "'Cormorant Garamond', 'Palatino', 'Georgia', serif",
        animation: 'tutorialPulse 3s ease-in-out infinite',
        textAlign: 'center',
      }}>
        Begin
      </div>
    </div>
  );
}

// ── Slides ──

const slides: TutorialSlide[] = [
  {
    title: 'The Network',
    subtitle: 'Nodes vibrate at different frequencies',
    visual: <NetworkVisual />,
  },
  {
    title: 'Harmony',
    subtitle: 'When a pathway is satisfied, it glows steadily',
    visual: <HarmonyVisual />,
  },
  {
    title: 'The Strike',
    subtitle: 'Tap any node to begin',
    visual: <StrikeVisual />,
  },
  {
    title: 'Spiral Propagation',
    subtitle: 'Distant nodes change more than near ones',
    visual: <PropagationVisual />,
  },
  {
    title: 'Resonance',
    subtitle: 'When all pathways are in harmony, you win',
    visual: <ResonanceVisual />,
  },
  {
    title: '',
    subtitle: '',
    visual: <BeginVisual />,
  },
];

// ── Main Component ──

export function Tutorial({ onComplete }: TutorialProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [visible, setVisible] = useState(false);

  // Fade in on mount
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const advance = useCallback(() => {
    if (isTransitioning) return;

    if (currentSlide >= slides.length - 1) {
      // Final slide — dismiss tutorial
      setVisible(false);
      setTimeout(() => onComplete(), 300);
      return;
    }

    setIsTransitioning(true);
    // Brief fade out, then advance
    setTimeout(() => {
      setCurrentSlide((prev) => prev + 1);
      setIsTransitioning(false);
    }, 300);
  }, [currentSlide, isTransitioning, onComplete]);

  const handleSkip = useCallback(() => {
    setVisible(false);
    setTimeout(() => onComplete(), 300);
  }, [onComplete]);

  const slide = slides[currentSlide];
  if (!slide) return null;

  return (
    <div
      style={{
        ...backdropStyle,
        opacity: visible ? 1 : 0,
      }}
      onClick={advance}
      role="dialog"
      aria-label="Tutorial"
    >
      {/* Skip button */}
      <button
        style={skipButtonStyle}
        onClick={(e) => {
          e.stopPropagation();
          handleSkip();
        }}
        aria-label="Skip tutorial"
        onMouseEnter={(e) => {
          e.currentTarget.style.color = 'rgba(232,232,228,0.6)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = 'rgba(232,232,228,0.3)';
        }}
      >
        Skip
      </button>

      {/* Slide content */}
      <div
        style={{
          ...slideContentStyle,
          opacity: isTransitioning ? 0 : 1,
          transform: isTransitioning ? 'translateY(10px)' : 'translateY(0)',
        }}
      >
        {/* Visual area */}
        <div style={visualAreaStyle}>
          {slide.visual}
        </div>

        {/* Text */}
        {slide.title && (
          <h2 style={titleStyle}>{slide.title}</h2>
        )}
        {slide.subtitle && (
          <p style={subtitleStyle}>{slide.subtitle}</p>
        )}
      </div>

      {/* Progress dots */}
      <div style={dotsContainerStyle}>
        {slides.map((_, i) => (
          <div
            key={i}
            style={{
              ...dotStyle,
              background: i === currentSlide
                ? 'rgba(199, 125, 255, 0.8)'
                : 'rgba(232,232,228,0.15)',
              boxShadow: i === currentSlide
                ? '0 0 6px rgba(199, 125, 255, 0.5)'
                : 'none',
            }}
          />
        ))}
      </div>

      {/* Tap to continue */}
      {currentSlide < slides.length - 1 && (
        <div style={tapContinueStyle}>
          tap to continue
        </div>
      )}
    </div>
  );
}

// ── Styles ──

const backdropStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  background: 'rgba(5, 5, 16, 0.95)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 200,
  cursor: 'pointer',
  transition: 'opacity 0.3s ease',
  userSelect: 'none',
};

const skipButtonStyle: React.CSSProperties = {
  position: 'absolute',
  top: '16px',
  right: '20px',
  background: 'none',
  border: 'none',
  color: 'rgba(232,232,228,0.3)',
  fontSize: '13px',
  fontWeight: 400,
  letterSpacing: '0.06em',
  cursor: 'pointer',
  padding: '8px 12px',
  fontFamily: "system-ui, -apple-system, sans-serif",
  transition: 'color 0.2s ease',
  outline: 'none',
  zIndex: 10,
};

const slideContentStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '20px',
  maxWidth: '340px',
  width: '90%',
  transition: 'opacity 0.3s ease, transform 0.3s ease',
};

const visualAreaStyle: React.CSSProperties = {
  width: '240px',
  height: '240px',
  position: 'relative',
  flexShrink: 0,
};

const visualContainerStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  position: 'relative',
};

const titleStyle: React.CSSProperties = {
  fontSize: '24px',
  fontWeight: 300,
  color: '#e8e8e4',
  letterSpacing: '0.15em',
  textShadow: '0 0 20px rgba(199, 125, 255, 0.5), 0 0 40px rgba(114, 9, 183, 0.3)',
  margin: 0,
  textAlign: 'center',
  fontFamily: "'Cormorant Garamond', 'Palatino', 'Georgia', serif",
};

const subtitleStyle: React.CSSProperties = {
  fontSize: '14px',
  color: 'rgba(232,232,228,0.5)',
  fontStyle: 'italic',
  margin: 0,
  textAlign: 'center',
  lineHeight: 1.5,
  fontFamily: "system-ui, -apple-system, sans-serif",
};

const dotsContainerStyle: React.CSSProperties = {
  position: 'absolute',
  bottom: '60px',
  display: 'flex',
  gap: '10px',
};

const dotStyle: React.CSSProperties = {
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  transition: 'all 0.3s ease',
};

const tapContinueStyle: React.CSSProperties = {
  position: 'absolute',
  bottom: '30px',
  fontSize: '12px',
  color: 'rgba(232,232,228,0.2)',
  fontFamily: "system-ui, -apple-system, sans-serif",
  letterSpacing: '0.1em',
  animation: 'tutorialPulse 3s ease-in-out infinite',
};

const tutorialNodeStyle: React.CSSProperties = {
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  flexShrink: 0,
};
